/**
 * Wire contract for the markdown-mode conversion & sync tracer.
 *
 * This file is the **single source of truth** for the trace event schema
 * described in `docs/devtools-tracing-design.md` §3. Both the producer (the
 * in-plugin tracer + instrumentation) and any consumer (Layer 1 console API,
 * Layer 2 in-app panel, Layer 3 external devtools client) import these types
 * so they cannot drift.
 *
 * ## Wire format
 *
 * Every event is **plain JSON** — it survives `structuredClone` /
 * `postMessage` / `JSON.stringify` losslessly. The `DocNode`, mdast `Root`,
 * and serialized `Step[]` fields are all already-serialized JSON, never live
 * ProseMirror/mdast class instances. This is what lets a Chrome DevTools
 * extension (a separate context, possibly a separate repo) consume the same
 * events the in-page console API sees.
 *
 * ## Portability note (for an out-of-AFM consumer, e.g. a Chrome extension)
 *
 * The imported types below are **type-only** and describe JSON shapes:
 *   - `DocNode` — ADF document JSON (`@atlaskit/adf-schema`)
 *   - `MdastRoot` — mdast tree JSON (derived from `mdast-util-from-markdown`)
 *   - `MarkdownSourceAnnotationRange` — `{ start, end, ... }` ranges
 * A consumer outside the monorepo can either install those typings or vendor a
 * copy of this file and replace the three imports with structural equivalents
 * (`unknown` / `Record<string, unknown>`). The runtime payloads are identical
 * either way.
 */
import type { fromMarkdown } from 'mdast-util-from-markdown';

import type { DocNode } from '@atlaskit/adf-schema';
import type { MarkdownSourceAnnotationRange } from '@atlassian/mdast-util-from-pm/types';

/**
 * The mdast tree JSON. Derived from `mdast-util-from-markdown`'s return type
 * (an mdast `Root`) rather than importing the `mdast` / `@types/mdast` package
 * directly, so the contract depends only on packages this plugin already
 * declares. An out-of-AFM consumer can replace this with `unknown` or
 * `@types/mdast`'s `Root`.
 */
export type MdastRoot = ReturnType<typeof fromMarkdown>;

/** One per top-level action; minted by the orchestrator, never by a helper. */
export type TraceId = string;

/**
 * Identifies one top-level user action (e.g. a single source-view edit), not
 * one conversion. The orchestrator mints it once when the action begins and
 * threads it into every conversion/diff hook so the parse, round-trip compare,
 * diff/replace, and final sync decision all join under one id.
 */
export type TraceContext = {
	id: TraceId;
	trigger: Trigger;
};

export type Trigger =
	| 'snapshot' // preview → source on view switch (adfToMarkdown)
	| 'live-tap' // debounced PM docChanged pushed to live ADF
	| 'source-change' // debounced CM6 edit → writeback
	| 'commit' // unmount / revision change → final writeback
	| 'manual'; // invoked from console/example for a one-off conversion

export type ConvertDirection = 'md->adf' | 'adf->md';

/** A captured error from a branch that the pipeline currently swallows. */
export type TraceError = {
	message: string;
	stack?: string;
};

/**
 * A pure conversion, either direction, with the intermediate mdast captured —
 * the tree `markdownToAdfViaMdast` builds and currently throws away.
 */
export type ConvertEvent = {
	direction: ConvertDirection;
	durationMs: number;
	/** Present when the conversion threw (e.g. `markdownToAdf` parse error). */
	error?: TraceError;
	id: TraceId;
	input: { adf?: DocNode; markdown?: string };
	kind: 'convert';
	/** The intermediate mdast tree (md->adf only). JSON, not a live tree. */
	mdast?: MdastRoot;
	output: {
		adf?: DocNode;
		annotations?: MarkdownSourceAnnotationRange[];
		markdown?: string;
	};
	trigger: Trigger;
	ts: number;
};

/** The sync decision that actually ran in the orchestrator. */
export type SyncDecision =
	| 'replaced' // replaceWith dispatched (legacy path)
	| 'diff-applied' // applyDiffSteps dispatched (reconciler path)
	| 'skipped-equivalent' // round-trip markdown matched → no-op
	| 'echo-filtered' // own-writeback / Node.eq guard ate it
	| 'parse-failed' // markdownToAdf threw
	| 'reseeded' // baseline abandoned & reset to live doc (reconciler path)
	| 'no-op'; // diffNodes produced zero steps

export type WritebackPath = 'replaceWith' | 'applyDiffSteps' | null;

/**
 * Why the reconciler abandoned its diff baseline and reset it to the live doc.
 * Reconciler-path-only; lives nested under `SyncEvent.reconciler` because
 * re-seeding never happens on the legacy path.
 */
export type ReseedReason =
	| 'parse-error' // markdownToAdf threw; baseline reset, user's typing wins
	| 'round-trip-match' // live doc already serializes to target md; absorb remote changes
	| 'rebase-failure' // a Step didn't survive rebase/map; recover by resetting baseline
	| 'post-writeback'; // normal reset after a successful diff-applied

/**
 * Reconciler-path-only context, present when
 * `decision === 'diff-applied' | 'reseeded' | 'no-op'`.
 */
export type ReconcilerContext = {
	/** The resulting document (ADF) after the diff steps were applied. */
	afterAdf?: DocNode;
	/** The live document (ADF) before the diff steps were applied. */
	beforeAdf?: DocNode;
	/** Mark types the diff ignores, e.g. `['annotation']`. */
	ignoredMarkTypes?: string[];
	/** Size of the non-own collab mapping the steps rebased through. */
	interveningStepCount?: number;
	/** e.g. 'step did not survive rebase' | 'schemas differ'. */
	reconcilerError?: string;
	reseedReason?: ReseedReason;
	stepCount?: number;
	/** `tr.steps.map(s => s.toJSON())` — the surgical payload (JSON). */
	steps?: unknown[];
};

export type SyncEvent = {
	/** Links to the ConvertEvent(s) that fed this decision. */
	convertId?: TraceId;
	decision: SyncDecision;
	/** Active feature gates at decision time. */
	gates: Record<string, boolean>;
	id: TraceId;
	kind: 'sync';
	note?: string;
	path: WritebackPath;
	reconciler?: ReconcilerContext;
	trigger: Trigger;
	ts: number;
};

/** The union stored in the ring buffer and returned by the query API. */
export type TraceEvent = ConvertEvent | SyncEvent;

/*
 ---------------------------------------------------------------------------
 Hook payloads — what instrumentation sites hand the tracer. The tracer fills
 in the orchestrator-owned fields (`id`, `trigger`, `ts`) and the `gates`
 snapshot, so a conversion helper or a sync branch only supplies the facts it
 actually knows. See design doc §3 "Trace-context ownership".
 ---------------------------------------------------------------------------
*/

/** What a conversion helper emits; `id`/`trigger`/`ts` are added by the tracer. */
export type ConvertTracePayload = Omit<ConvertEvent, 'kind' | 'id' | 'trigger' | 'ts'>;

/**
 * The hook a conversion helper calls. The orchestrator binds its `TraceContext`
 * into this before passing it in, so the helper never mints ids or knows the
 * trigger — it just reports the conversion it performed.
 */
export type ConvertTraceHook = (payload: ConvertTracePayload) => void;

/**
 * What a sync branch emits; `id`/`trigger`/`ts` are added by the tracer and
 * `gates` defaults to the tracer's gate snapshot when omitted.
 */
export type SyncEventInput = Omit<SyncEvent, 'kind' | 'id' | 'trigger' | 'ts' | 'gates'> & {
	gates?: Record<string, boolean>;
};

/**
 * One trace context with the tracer already bound in, handed to a single
 * top-level action so its instrumentation reads as `trace?.sync(...)` /
 * `trace?.convert(...)` without re-threading the tracer or the `TraceContext`.
 * Obtained from `MarkdownModeTracer.begin(trigger)`, which returns `undefined`
 * when the gate is off or recording is disabled — so a callsite's `trace?.`
 * collapses to a no-op with no payload ever built. `sync` auto-fills
 * `convertId` (always equal to `id`) so branches don't hand-copy it.
 */
export type TraceSession = {
	convert: (build: () => ConvertTracePayload) => void;
	convertHook: () => ConvertTraceHook;
	readonly id: TraceId;
	sync: (build: () => SyncEventInput) => void;
};

/** Optional filter accepted by `getEvents`. */
export type TraceEventFilter = {
	decision?: SyncDecision;
	id?: TraceId;
	kind?: TraceEvent['kind'];
	trigger?: Trigger;
};

/**
 * The console API attached to `window.__markdownModeDebugger__` when the gate
 * is on (Layer 1). Consumers can type the global against this.
 */
export type MarkdownModeDebuggerApi = {
	clear: () => void;
	disable: () => void;
	enable: () => void;
	getEvents: (filter?: TraceEventFilter) => TraceEvent[];
	getLastSync: () => SyncEvent | undefined;
	/** All events for one action, joined by TraceId. */
	getTrace: (id: TraceId) => TraceEvent[];
	isEnabled: () => boolean;
	/** Run a one-off conversion both ways, diffed (a 'manual'-trigger probe). */
	roundTrip: (input: string | DocNode) => unknown;
};

/*
 ---------------------------------------------------------------------------
 Layer 3 — postMessage bridge envelope (see design doc §5).
 A consumer subscribes; the tracer replays the buffer then streams events.
 ---------------------------------------------------------------------------
*/

/** Source tag on messages the tracer emits. */
export const TRACER_MESSAGE_SOURCE = 'atl-md-mode-tracer' as const;
/** Source tag a client must set on its subscribe/unsubscribe messages. */
export const TRACER_CLIENT_MESSAGE_SOURCE = 'atl-md-mode-tracer-client' as const;

/** Message a client posts to start/stop receiving events. */
export type TracerClientMessage = {
	source: typeof TRACER_CLIENT_MESSAGE_SOURCE;
	type: 'subscribe' | 'unsubscribe';
};

/** Message the tracer posts to subscribed clients (same-origin only). */
export type TracerMessage =
	/**
	 * Broadcast once when the tracer's message listener comes online. It carries
	 * no event payload — it exists purely to defeat the handshake race: a client
	 * (e.g. a content script injected at `document_start`) usually loads *before*
	 * the editor mounts and creates the tracer, so its initial `subscribe` lands
	 * before any listener exists. On hearing `online`, such a client must
	 * (re-)subscribe; the tracer then replays its buffer.
	 */
	| { source: typeof TRACER_MESSAGE_SOURCE; type: 'online' }
	| { event: TraceEvent; source: typeof TRACER_MESSAGE_SOURCE; type: 'replay' }
	| { event: TraceEvent; source: typeof TRACER_MESSAGE_SOURCE; type: 'event' };
